import React from 'react';
import { Link } from 'react-router-dom';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faGraduationCap,
	faComments,
	faCheck,
	faCalendarCheck,
	faWallet,
	faMicrophoneLines,
	faStar,
	faChevronDown,
} from '@fortawesome/pro-regular-svg-icons';

import './Lessons.css';

// PACKS REMOVED 2026-08-18 (card cYdGGTb4kI35hVRGFA4B). All four buy paths — Voice
// 4-Pack, Voice 8-Pack, Intro Piano for the Singer 5-Pack, Intro Guitar for the
// Singer 5-Pack — came off this page, along with the pack fetch, the focused
// PackPurchaseForm checkout, the ?status=success/cancelled banners and the Meta
// Pixel Purchase hook that only that checkout fired.
//
// WHY, and why NOTHING replaces them: a pack bought today cannot be delivered —
// there is nowhere to put five piano or eight voice lessons between the summer
// run ending Aug 21 and the school year starting Aug 31 — and CYV has never sold
// voice packs during the school year (core.md -> Lessons). A priced, buttoned
// per-lesson option during the school year competes with what tuition exists to
// do, so the deliberate answer is tuition, with single lessons staying
// case-by-case and unadvertised. Do not "fix" this gap by adding a school-year
// per-lesson product; that is a post-cutover conversation, not a website change.
//
// The server side is closed too: createPackCheckout refuses any pack whose
// Firestore status is not 'open', and all four docs were flipped to 'archived'
// at deploy. The Cloud Function, the packs collection, packService's admin reads
// and the .pk card styles in Lessons.css are all LEFT IN PLACE for a summer-2027
// restore. Stripe carries no Product or Price for these packs at all — each
// checkout minted an inline price_data — so there is nothing archived there.

const LessonPacks = () => {
	return (
		<main className='cyv lessons-page'>
			<Seo
				title='Private Lessons | Color Your Voice'
				description='Private one-on-one voice lessons at Color Your Voice in Burnsville, MN — a school-year program with a standing weekly time, studio recitals, and a teaching artist who gets to know your singer.'
				canonical={`${SITE}/lessons`}
			/>

			{/* ---- Hero (centered) ---- */}
			<header className='pg-hero'>
				<span className='eyebrow'>Private lessons</span>
				<h1 className='brand-heading'>Voice, piano &amp; guitar</h1>
				<p>
					One-on-one, paced to your child — with teaching artists who get to
					know exactly how they grow. We teach <strong>voice</strong> across the
					school year, and we're enrolling now for 2026–27.
				</p>
				<div className='pg-hero__cta'>
					<Link className='btn-cyv-teal' to='/contact'>
						Get started
					</Link>
				</div>
			</header>

			{/* ---- Two-path overview ---- */}
			<section className='ls'>
				<div className='ls-paths'>
					{/* Left — School-year core (copy per core.md) */}
					<article className='cyv-card ls-card' style={{ '--accent': 'var(--grad-lessons)' }}>
						<span className='accent-bar' />
						<span className='ls-card__tag tag-core'>
							<FontAwesomeIcon icon={faGraduationCap} /> School-year lessons
						</span>
						<h3>One weekly time, all year</h3>
						<p className='ls-card__sub'>
							Our core program: a standing weekly slot from fall through spring,
							drawing on the full range of our teaching artists' expertise — building real,
							lasting growth, capped off with our recitals.
						</p>
						<ul className='ls-list'>
							<li><FontAwesomeIcon icon={faCheck} /><span>One-on-one voice lessons</span></li>
							<li><FontAwesomeIcon icon={faCheck} /><span>Your own weekly time slot, all year long</span></li>
							<li><FontAwesomeIcon icon={faCheck} /><span>Studio recitals &amp; performance opportunities</span></li>
							<li><FontAwesomeIcon icon={faCheck} /><span>Make-up credits with 24 hours' notice</span></li>
						</ul>
						<div className='ls-card__cta'>
							<Link className='btn-cyv-teal' to='/contact' style={{ width: '100%', minHeight: '48px' }}>
								Get started
							</Link>
							<p className='ls-card__fine'>Enroll by inquiry · waitlisted when full</p>
						</div>
					</article>

					{/* Right — Talk to us. This slot held the summer-packs summary until
					    2026-08-08 and the free-trial card until 2026-09-03, when /trial was
					    deleted (trials are offered by hand until reopened publicly). A
					    conversation is now the try-before-you-commit path. */}
					<article className='cyv-card ls-card ls-card--trial' style={{ '--accent': 'var(--grad-summer)' }}>
						<span className='accent-bar' />
						<span className='ls-card__tag tag-summer'>
							<FontAwesomeIcon icon={faComments} /> Not sure yet?
						</span>
						<h3>Talk it through first</h3>
						<p className='ls-card__sub'>
							Tell us about your singer — age, experience, what they love to sing —
							and we'll help you find the right fit before you commit to the year.
						</p>
						<div className='ls-card__cta'>
							<Link
								className='btn-cyv-ghost'
								to='/contact'
								style={{ width: '100%', minHeight: '48px' }}>
								Reach out
							</Link>
							<p className='ls-card__fine'>
								We reply within a day or two
							</p>
						</div>
					</article>
				</div>
			</section>

			{/* ---- How school-year lessons work (centered) ---- */}
			<section className='ls-how'>
				<h2 className='brand-heading'>How school-year lessons work</h2>
				<div className='ls-how__grid'>
					<div className='ls-how__item'>
						<span className='ls-how__ic'><FontAwesomeIcon icon={faCalendarCheck} /></span>
						<h4>One standing time</h4>
						<p>Your child keeps the same weekly slot all year — growing with the full range of our teaching artists.</p>
					</div>
					<div className='ls-how__item'>
						<span className='ls-how__ic'><FontAwesomeIcon icon={faWallet} /></span>
						<h4>One simple tuition</h4>
						<p>One tuition for the year, spread across monthly payments — reach out for current rates.</p>
					</div>
					<div className='ls-how__item'>
						<span className='ls-how__ic'><FontAwesomeIcon icon={faMicrophoneLines} /></span>
						<h4>Voice, year-round</h4>
						<p>School-year lessons are voice; piano and guitar join us for the summer.</p>
					</div>
					<div className='ls-how__item'>
						<span className='ls-how__ic'><FontAwesomeIcon icon={faStar} /></span>
						<h4>Recitals &amp; stage time</h4>
						<p>Regular recitals give every student a goal and a moment to shine.</p>
					</div>
				</div>
			</section>

			{/* ---- FAQ teaser ---- */}
			<section className='ls-faq'>
				<h2>Lesson questions</h2>
				<details open>
					<summary>
						How is school-year tuition structured? <FontAwesomeIcon icon={faChevronDown} />
					</summary>
					<div className='ls-faq__a'>
						It's one simple tuition for the school year, split across monthly
						payments. <Link to='/contact'>Reach out</Link> for current school-year
						rates. Summer lessons are optional and billed separately.
					</div>
				</details>
				<details>
					<summary>
						Can we just do a single lesson? <FontAwesomeIcon icon={faChevronDown} />
					</summary>
					<div className='ls-faq__a'>
						Our school-year lessons are a year-long journey, so we don't sell
						individual drop-in lessons to the public. Already enrolled? You can
						book single make-ups through your portal. Just
						want to talk it through? <Link to='/contact'>Reach out</Link> and
						we'll find the right fit.
					</div>
				</details>
				<details>
					<summary>
						What if my child misses a lesson? <FontAwesomeIcon icon={faChevronDown} />
					</summary>
					<div className='ls-faq__a'>
						School-year lessons need 24 hours' notice for a make-up credit,
						subject to availability. Full details live in our{' '}
						<Link to='/faq'>FAQ</Link> and policies.
					</div>
				</details>
				<div className='ls-faq__all'>
					<Link to='/faq'>See all FAQs →</Link>
				</div>
			</section>

			{/* ---- Closing CTA (centered) ---- */}
			<section className='ls-cta'>
				<div className='ls-cta__in'>
					<span className='accent-bar' />
					<h2 className='brand-heading'>Ready to get started?</h2>
					<div className='ls-cta__btns'>
						<Link className='btn-cyv-teal' to='/contact'>
							Contact us
						</Link>
					</div>
					<p>Reach out and we'll get your young artist set up.</p>
				</div>
			</section>
		</main>
	);
};

export default LessonPacks;
